import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { Geometry, Position } from 'geojson';
import { AuthService } from '../../auth/auth.service';
import { LinkingCrudService } from '../../linking/linking.service';
import { TooltipDirective } from '../../components/qm-directives/tooltip-directive/tooltip.directive';
import { MapElement } from '../../shared/map/map-element.model';
import { MapService } from '../../shared/map/map.service';
import { AlarmMessage } from '../alarm-message';
import { AlarmService } from '../alarm.service';
import { isAlarmActiveForUser } from '../alarm.utils';
import { MappingService } from '~app/linking/mapping.service';

@Component({
  selector: 'qm-alarm-item-menu',
  templateUrl: './alarm-item-menu.component.html',
  styleUrls: ['./alarm-item-menu.component.scss']
})
export class AlarmItemMenuComponent implements OnInit {

  @Input() public alarm: AlarmMessage;
  @Output() public alarmRemoved = new EventEmitter<void>();
  @ViewChild('ttip') private ttip: TooltipDirective;
  public locateRows: { label: string, feature?: MapElement<Geometry>, center?: Position }[] = [];
  public locateLoading = false;
  public disabledByUser: boolean;
  public hasUserRightsToDisableAlarm: boolean = this.authService.hasPermission('alarmmanipulator');

  constructor(
    private readonly alarmService: AlarmService,
    private readonly mapService: MapService,
    private readonly linkingCrudService: LinkingCrudService,
    private readonly mappingService: MappingService,
    private readonly authService: AuthService,
    private readonly cdr: ChangeDetectorRef,
  ) { }

  ngOnInit(): void {
    this.disabledByUser = !isAlarmActiveForUser(this.alarm?.metadata);
  }

  public setAlarmActivity(alarm: AlarmMessage, isActive: boolean) {
    if (!this.hasUserRightsToDisableAlarm) {
      return;
    }
    this.ttip.close();
    this.alarmService.setAlarmActiveStatus(alarm.metadata?._id, isActive)
      .subscribe({
        next: () => this.alarmRemoved.next(),
        error: console.warn
      });
  }

  public focusMarker(locateRow: AlarmItemMenuComponent['locateRows'][0]) {
    if (locateRow.feature?.geometry.type === 'Point') {
      // Make sure marker's layer is visible
      locateRow.feature.layer.setVisibility(true);
      // Set marker selected / highlighted
      this.mapService.changeCurrentFeature(locateRow.feature);
      const [lng, lat] = locateRow.feature.geometry.coordinates;
      this.mapService.flyTo({ lng, lat });
    } else if (locateRow.center) {
      this.mapService.changeCurrentFeature(null);
      const [lng, lat] = locateRow.center;
      this.mapService.flyTo({ lng, lat });
    }
  }

  public openMenu(event: MouseEvent, tooltip: TooltipDirective) {
    this.onTooltipOpen();
    tooltip.open({
      alarm: this.alarm,
      event
    });
  }

  private async onTooltipOpen() {
    this.locateLoading = true;
    this.locateRows = [];
    this.cdr.markForCheck();
    if (this.alarm.metadata.type === 'vrShiftConflict'
      || this.alarm.metadata.type === 'vrTrainConflict') {
      // Conflict alarms have two locations
      const id1 = this.alarm.metadata.payload.trainDate + '_' + this.alarm.metadata.payload.tripNumber;
      const id2 = this.alarm.metadata.payload.dependentTrainDate + '_' + this.alarm.metadata.payload.dependentTripNumber;
      const f1 = this.mapService.getFeatureById(id1);
      const f2 = this.mapService.getFeatureById(id2);
      this.locateRows = [
        ...this.locateRows,
        ...f1 ? [{ label: `${f1.properties?.title}${f1.properties?.subTitle}`, feature: f1 }] : [],
        ...f2 ? [{ label: `${f2.properties?.title}${f2.properties?.subTitle}`, feature: f2 }] : [],
      ];
    } else if (this.alarm.metadata.type === 'miproMeasure') {
      const id = AlarmService.getIdentifier(this.alarm.metadata);
      for (const linking of await this.linkingCrudService.getLinkingsForSourceId(id)) {
        const feature = this.mapService.getFeatureById(linking.regoId);
        this.locateRows = [
          ...this.locateRows,
          ...feature ? [{ label: feature.properties.description, feature }] : []
        ];
      }
    } else if (this.alarm.metadata.type === 'valmetDNA') {
      const id = AlarmService.getIdentifier(this.alarm.metadata);
      for (const linking of await this.linkingCrudService.getLinkingsForSourceId(id)) {
        const definition = await this.mappingService.getRegoFeatureDefinition(linking.regoId);
        this.locateRows = [
          ...this.locateRows,
          ...definition ? [{ label: definition.title, center: definition.center }] : []
        ];
      }
    } else { // Single location
      const id = AlarmService.getIdentifier(this.alarm.metadata);
      const feature = this.mapService.getFeatureById(id);
      if (!!feature) {
        const label = `${feature.properties?.title} ${feature.properties?.subTitle}`;
        this.locateRows = [
          ...this.locateRows,
          ...feature ? [{ label, feature }] : []
        ];
      }
    }
    this.locateLoading = false;
    this.cdr.markForCheck();
  }

}
